import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { developerApi } from '../../api/developer.api';
import { useAuthStore } from '../../store/auth.store';
import type {
  AppPermissionHolderDto,
  ProviderDeveloperDto,
  AppRole,
} from '../../types';

export interface ProviderDevelopersParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface ProviderDevelopersResponse {
  items: ProviderDeveloperDto[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Fetch developers belonging to current provider (Super Admin only).
 * Endpoint: GET /v1/miniapp/developers
 */
export function useProviderDevelopers(
  params?: ProviderDevelopersParams,
  options?: {
    enabled?: boolean;
    retry?: boolean | number | ((failureCount: number, error: any) => boolean);
    staleTime?: number;
  }
) {
  return useQuery<ProviderDevelopersResponse>({
    queryKey: ['developer', 'provider-developers', params],
    queryFn: async () => {
      const { data } = await developerApi.get('/v1/miniapp/developers', {
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
          ...(params?.search ? { search: params.search } : {}),
        },
      });

      if (data && Array.isArray(data.items)) {
        return {
          items: data.items,
          total: data.total ?? data.items.length,
          page: data.page ?? 1,
          limit: data.limit ?? 20,
          totalPages: data.totalPages ?? 1,
        };
      }

      if (Array.isArray(data)) {
        return {
          items: data,
          total: data.length,
          page: 1,
          limit: data.length,
          totalPages: 1,
        };
      }

      return {
        items: [],
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 1,
      };
    },
    enabled: options?.enabled ?? true,
    retry:
      options?.retry ??
      ((failureCount, error: any) => {
        // Do not retry on 403 Forbidden or 401 Unauthorized
        if (error?.response?.status === 403 || error?.response?.status === 401) {
          return false;
        }
        return failureCount < 2;
      }),
    staleTime: options?.staleTime ?? 30_000,
  });
}

/**
 * Hook to verify whether the current authenticated developer is a Super Admin.
 * Returns true if user has super admin privileges, false otherwise.
 */
export function useIsDeveloperSuperAdmin(): boolean {
  const { user } = useAuthStore();

  const isDev = user?.role === 'partner_dev';
  const needsCheck = Boolean(isDev && !user?.dev_role);

  // If not explicitly recorded on auth state, detect via /v1/miniapp/developers permission check
  const { data, isSuccess, isError, isLoading } = useProviderDevelopers(
    { limit: 1 },
    {
      enabled: needsCheck,
      retry: false,
      staleTime: 60_000,
    }
  );

  if (!isDev) return false;
  if (user?.dev_role === 'super') return true;
  if (user?.dev_role === 'member') return false;

  if (isLoading || isError) return false;
  return isSuccess && !!data;
}

/**
 * Fetch developers granted permissions on a specific mini-app (Super Admin only).
 * Endpoint: GET /v1/miniapp/apps/{appId}/permissions
 */
export function useAppPermissions(appId: string, options?: { enabled?: boolean }) {
  return useQuery<AppPermissionHolderDto[]>({
    queryKey: ['developer', 'app-permissions', appId],
    queryFn: async () => {
      const { data } = await developerApi.get(`/v1/miniapp/apps/${appId}/permissions`);
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    },
    enabled: !!appId && appId !== 'new' && appId !== 'create' && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}

export interface GrantAppPermissionPayload {
  appId: string;
  developerId: string;
  role?: AppRole;
}

/**
 * Grant app permission to a developer member (Super Admin only).
 * Endpoint: PUT /v1/miniapp/apps/{appId}/permissions/{developerId}
 */
export function useGrantAppPermission() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, GrantAppPermissionPayload>({
    mutationFn: async ({ appId, developerId, role = 'maintainer' }) => {
      await developerApi.put(`/v1/miniapp/apps/${appId}/permissions/${developerId}`, {
        role,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'app-permissions', variables.appId] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'apps'] });
    },
  });
}

/**
 * Revoke app permission from a developer member (Super Admin only).
 * Endpoint: DELETE /v1/miniapp/apps/{appId}/permissions/{developerId}
 */
export function useRevokeAppPermission() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { appId: string; developerId: string }>({
    mutationFn: async ({ appId, developerId }) => {
      await developerApi.delete(`/v1/miniapp/apps/${appId}/permissions/${developerId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'app-permissions', variables.appId] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'apps'] });
    },
  });
}
